import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Tooltip, 
  Divider,
  Alert
} from '@mui/material';
import { useQuery, useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { GET_TASKS, UPDATE_TASK } from '../../../Dashboard/components/TaskSection';
import { parseBackendDate } from '../../../Dashboard/components/utils';

interface DailyTasksProps {
  selectedDate: Dayjs;
}

const DailyTasks: React.FC<DailyTasksProps> = ({ selectedDate }) => {
  const { data, loading, error } = useQuery(GET_TASKS, { 
    variables: { archived: false },
    fetchPolicy: 'cache-and-network'
  });
  
  const [updateTask] = useMutation(UPDATE_TASK, {
    refetchQueries: ['GetTasks', 'GetTasksHistory']
  });

  const [localTasks, setLocalTasks] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (data?.getTasks) {
      setLocalTasks(data.getTasks);
    }
  }, [data]);

  if (error) return <Alert severity="error">Failed to load tasks: {error.message}</Alert>;

  if (loading && localTasks.length === 0) return null;

  const dateStr = selectedDate.format('YYYY-MM-DD');
  const dailyTasks = localTasks.filter((task: any) => {
    const taskDate = parseBackendDate(task.endDate);
    return taskDate && taskDate.format('YYYY-MM-DD') === dateStr;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'FINISHED': return 'success';
      case 'STARTED': return 'warning';
      default: return 'error';
    }
  };

  const nextStatus = (current: string) => {
    if (current === 'NOT_STARTED') return 'STARTED';
    if (current === 'STARTED') return 'FINISHED';
    return 'NOT_STARTED';
  };

  const handleToggleStatus = (task: any) => {
    const targetStatus = nextStatus(task.status);
    
    // 1. Update local state immediately for instant feedback
    setLocalTasks(prev => 
      prev.map(t => t.id === task.id ? { ...t, status: targetStatus } : t)
    );

    // 2. Fire update mutation asynchronously in the background
    updateTask({ 
      variables: { 
        id: task.id, 
        input: { status: targetStatus } 
      } 
    }).catch(error => {
      // Revert local state if mutation fails
      setLocalTasks(prev => 
        prev.map(t => t.id === task.id ? { ...t, status: task.status } : t)
      );
      console.error('Update failed:', error);
    });
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Tasks Due Today
      </Typography>

      {dailyTasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
          No tasks scheduled for this day.
        </Typography>
      ) : (
        <List sx={{ pt: 0 }}>
          {dailyTasks.map((task: any) => (
            <React.Fragment key={task.id}>
              <ListItem 
                disablePadding 
                sx={{ py: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Tooltip title="Toggle Status">
                    <Box 
                      onClick={() => handleToggleStatus(task)}
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        bgcolor: `${getStatusColor(task.status)}.main`,
                        cursor: 'pointer',
                        flexShrink: 0,
                        boxShadow: 1
                      }} 
                    />
                  </Tooltip>
                  <ListItemText 
                    primary={task.title} 
                    primaryTypographyProps={{ 
                      sx: { 
                        textDecoration: task.status === 'FINISHED' ? 'line-through' : 'none',
                        opacity: task.status === 'FINISHED' ? 0.6 : 1
                      } 
                    }}
                  />
                </Box>
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default DailyTasks;
